import { Box, makeStyles, Theme, Typography } from "@material-ui/core";
import Icon from "@material-ui/icons/CheckBox";
import moment from "moment-timezone";
import React, { FC, useEffect, useState } from "react";
import {
  AutocompleteInput,
  Datagrid,
  DateInput,
  Edit,
  EditProps,
  Filter,
  FunctionField,
  List,
  ListActionsProps,
  ListProps,
  Loading,
  ReferenceInput,
  Show,
  ShowActionsProps,
  ShowProps,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  TopToolbar,
  useListContext,
} from "react-admin";
import { useHistory, useLocation } from "react-router-dom";
import { BookingCard, DeliveryCard } from "../../components/cards";
import { AllocationChip, BookingChip } from "../../components/Chip";
import CrudBreadcrumb from "../../components/CrudBreadcrumb";
import ColumnToggleButton from "../../components/datagrid/ColumnToggleButton";
import FilterTabs from "../../components/datagrid/FilterTabs";
import GroupByToggleButton from "../../components/datagrid/GroupByToggleButton";
import GroupedDatagrid, {
  ColumnDef,
  GroupConfig,
} from "../../components/datagrid/GroupedDatagrid";
import StatusPopover from "../../components/datagrid/StatusPopover";
import {
  AllocationIdField,
  ProductInstanceIdField,
} from "../../components/fields";
import BookingField from "../../components/fields/BookingIdField";
import { useColumnVisibility } from "../../hooks/useColumnVisibility";
import { Allocation } from "../../types";
import AssignButton from "./actions/AssignButton";
import CancelButton from "./actions/CancelButton";
import FinishButton from "./actions/FinishButton";
import GotIssueButton from "./actions/GotIssueButton";
import ResolveIssueButton from "./actions/ResolveIssueButton";
import UnAssignButton from "./actions/UnAssignButton";
import {
  ALLOCATED_STATUSES,
  ALLOCATION_STATUS_NOT_ASSIGNED,
  AllocationStatus,
  ProductTypeList,
  UNALLOCATED_STATUSES,
} from "./util";

const useToolbarStyles = makeStyles((theme: Theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "flex-start",
    width: "100%",
    padding: "4px 0px",
  },
  row: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
  },
}));

const COLUMN_CHOICES = [
  { key: "allocation_id", label: "Allocation" },
  { key: "booking_id", label: "Booking ID", disabled: true },
  { key: "product_name", label: "Product Name", disabled: true },
  { key: "product_type", label: "Product Type" },
  { key: "business_unit", label: "Business Unit" },
  { key: "allocated_to", label: "Allocated To", disabled: true },
  { key: "surgery_date", label: "Surgery Date", disabled: true },
  { key: "allocation_status", label: "Allocation Status", disabled: true },
  { key: "product_set", label: "Product Set", disabled: true },
  { key: "set_current_loc", label: "Set Current Loc", disabled: true },
];

const STATUS_OPTIONS = Object.entries(AllocationStatus).map(([, value]) => ({
  id: value,
  label: value
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" "),
}));

const PRODUCT_TYPE_POPOVER_OPTIONS = ProductTypeList.map((c) => ({
  id: c.id,
  label: c.name,
}));

const STATUS_TABS = [
  {
    label: "Assigned",
    isActive: (fv: any) => fv.status === ALLOCATED_STATUSES.join(","),
    getFilters: () => ({ status: ALLOCATED_STATUSES.join(",") }),
  },
  {
    label: "Not Assigned",
    isActive: (fv: any) => fv.status === UNALLOCATED_STATUSES.join(","),
    getFilters: () => ({ status: UNALLOCATED_STATUSES.join(",") }),
  },
];

const DATE_TABS = [
  {
    label: "Today",
    getFilters: () => {
      const today = moment().format("YYYY-MM-DD");
      return { date_from: today, date_to: today };
    },
    isActive: (fv: any) => {
      const today = moment().format("YYYY-MM-DD");
      return fv.date_from === today && fv.date_to === today;
    },
  },
  {
    label: "Back",
    getFilters: () => ({
      date_to: moment().format("YYYY-MM-DD"),
    }),
    isActive: (fv: any) => {
      const today = moment().format("YYYY-MM-DD");
      return !fv.date_from && fv.date_to === today;
    },
  },
  {
    label: "Next",
    getFilters: () => ({
      date_from: moment().format("YYYY-MM-DD"),
    }),
    isActive: (fv: any) => {
      const today = moment().format("YYYY-MM-DD");
      return fv.date_from === today && !fv.date_to;
    },
  },
];

const DATE_FILTER_KEYS = ["date_from", "date_to"];
const STATUS_FILTER_KEYS = ["status"];

const GROUP_BY_OPTIONS = [
  { value: "allocated_to", label: "Allocated To" },
  { value: "product_name", label: "Product Name" },
  { value: "set_current_loc", label: "Set Current Loc" },
];

const getGroupSort = (
  groupBy: string,
): { field: string; order: "asc" | "desc" } => {
  switch (groupBy) {
    case "product_name":
      return { field: "product.name", order: "asc" };
    case "allocated_to":
      return { field: "booking.hospital_room.hospital.name", order: "asc" };
    case "set_current_loc":
      return { field: "product_instance.location.name", order: "asc" };
    default:
      return { field: "id", order: "desc" };
  }
};

const getGroupConfig = (groupBy: string): GroupConfig | undefined => {
  switch (groupBy) {
    case "allocated_to":
      return {
        getGroupKey: (record: any) =>
          record.booking?.hospital_room?.hospital?.name ?? "Unknown Hospital",
        getGroupLabel: (key: string) => key,
      };
    case "product_name":
      return {
        getGroupKey: (record: any) => record.product?.name ?? "Unknown Product",
        getGroupLabel: (key: string) => key,
      };
    case "set_current_loc":
      return {
        getGroupKey: (record: any) =>
          record.product_instance?.location?.name ?? "Unknown Location",
        getGroupLabel: (key: string) => key,
      };
    default:
      return undefined;
  }
};

const ListActions: FC<
  ListActionsProps & {
    visibleColumns: string[];
    onToggleColumn: (key: string) => void;
    onResetColumns: () => void;
    groupBy: string;
    onGroupByChange: (value: string) => void;
  }
> = (props) => {
  const toolbarClasses = useToolbarStyles();

  const {
    visibleColumns,
    onToggleColumn,
    onResetColumns,
    groupBy,
    onGroupByChange,
    filters,
    ...rest
  } = props;
  const { resource, showFilter, displayedFilters } = props;
  const { filterValues } = useListContext();

  return (
    <TopToolbar {...rest} className={toolbarClasses.root}>
      <Box className={toolbarClasses.row}>
        {/* LEFT: Date tabs + Status tabs */}
        <Box display="flex" alignItems="center" gridGap={15}>
          <FilterTabs tabs={DATE_TABS} clearKeys={DATE_FILTER_KEYS} />
          <FilterTabs tabs={STATUS_TABS} clearKeys={STATUS_FILTER_KEYS} />
          <StatusPopover options={STATUS_OPTIONS} />
          <StatusPopover
            options={PRODUCT_TYPE_POPOVER_OPTIONS}
            filterKey="product.type"
            label="Type"
          />
        </Box>

        <Box flexGrow={1} />

        {/* RIGHT: Show Columns + Group By + Add Filter */}
        <Box display="flex" alignItems="center" gridGap={4}>
          <ColumnToggleButton
            columns={COLUMN_CHOICES}
            visibleColumns={visibleColumns}
            onToggle={onToggleColumn}
            onReset={onResetColumns}
          />

          <GroupByToggleButton
            options={GROUP_BY_OPTIONS}
            value={groupBy}
            onChange={onGroupByChange}
          />

          {filters &&
            React.cloneElement(filters, {
              resource,
              showFilter,
              displayedFilters,
              filterValues,
              context: "button",
            })}
        </Box>
      </Box>
    </TopToolbar>
  );
};

const getStatusLabel = (status: string): string => {
  return status
    .split(",")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " "))
    .join(", ");
};

const getProductTypeLabel = (type: string): string => {
  const lookup = Object.fromEntries(
    PRODUCT_TYPE_POPOVER_OPTIONS.map((o) => [o.id, o.label]),
  );
  return type
    .split(",")
    .map((t) => lookup[t] ?? t)
    .join(", ");
};

const DatagridTitle: FC<{ groupBy?: string }> = ({ groupBy }) => {
  const { filterValues } = useListContext();
  const from = filterValues?.date_from;
  const to = filterValues?.date_to;
  const status = filterValues?.status;
  const productType = filterValues?.["product.type"];

  const hasDate = !!(from || to);
  const hasStatus = !!status;
  const hasProductType = !!productType;
  const groupLabel = GROUP_BY_OPTIONS.find((o) => o.value === groupBy)?.label;

  if (!hasDate && !hasStatus && !hasProductType && !groupLabel) return null;

  const today = moment().format("YYYY-MM-DD");
  const fmt = "DD/MM/YYYY";

  let dateText = "";
  if (hasDate) {
    if (from === today && to === today) {
      dateText = `Today — ${moment(from).format(fmt)}`;
    } else if (from && !to) {
      dateText = `From — ${moment(from).format(fmt)}`;
    } else if (!from && to) {
      dateText = `Until — ${moment(to).format(fmt)}`;
    } else {
      dateText = `${moment(from).format(fmt)} — ${moment(to).format(fmt)}`;
    }
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      gridGap={26}
      style={{
        padding: "8px 16px 7px 16px",
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
      }}
    >
      {hasDate && (
        <Typography variant="subtitle2" color="textPrimary">
          Surgery Date: {dateText}
        </Typography>
      )}
      {hasStatus && (
        <Typography variant="subtitle2" color="textPrimary">
          Allocation Status: {getStatusLabel(status)}
        </Typography>
      )}
      {hasProductType && (
        <Typography variant="subtitle2" color="textPrimary">
          Product Type: {getProductTypeLabel(productType)}
        </Typography>
      )}
      {groupLabel && (
        <Typography variant="subtitle2" color="textPrimary">
          Grouped by: {groupLabel}
        </Typography>
      )}
    </Box>
  );
};

const SurgeryDateField: FC<{ record: any }> = ({ record }) => {
  const { filterValues } = useListContext();
  const today = moment().format("YYYY-MM-DD");
  const isToday =
    filterValues?.date_from === today && filterValues?.date_to === today;
  const fmt = isToday ? "hh:mm A" : "DD/MM/YYYY - hh:mm A";

  return (
    <span>
      {record.booking?.date
        ? moment(record.booking.date).tz("Asia/Singapore").format(fmt)
        : ""}
    </span>
  );
};

const getColumns = (visibleColumns: string[]): ColumnDef[] => {
  const cols: ColumnDef[] = [];
  if (visibleColumns.includes("allocation_id"))
    cols.push({
      key: "allocation_id",
      label: "Allocation",
      render: (record: any) => <AllocationChip resource={record} />,
    });
  if (visibleColumns.includes("booking_id"))
    cols.push({
      key: "booking_id",
      label: "Booking",
      render: (record: any) => <BookingChip resource={record.booking} />,
    });
  if (visibleColumns.includes("product_name"))
    cols.push({
      key: "product_name",
      label: "Product Name",
      render: (record: any) => record.product?.name ?? "",
    });
  if (visibleColumns.includes("product_type"))
    cols.push({
      key: "product_type",
      label: "Product Type",
      render: (record: any) => record.product?.type ?? "",
    });
  if (visibleColumns.includes("business_unit"))
    cols.push({
      key: "business_unit",
      label: "Business Unit",
      render: (record: any) =>
        record.product_instance?.product?.business_unit?.name ?? "",
    });
  if (visibleColumns.includes("allocated_to"))
    cols.push({
      key: "allocated_to",
      label: "Allocated To",
      render: (record: any) => {
        const hospital = record.booking?.hospital_room?.hospital?.name;
        const room = record.booking?.hospital_room?.name;
        return hospital && room ? `${hospital} (${room})` : "";
      },
    });
  if (visibleColumns.includes("surgery_date"))
    cols.push({
      key: "surgery_date",
      label: "Surgery Date",
      render: (record: any) => <SurgeryDateField record={record} />,
    });
  if (visibleColumns.includes("allocation_status"))
    cols.push({
      key: "allocation_status",
      label: "Allocation Status",
      render: (record: any) =>
        ALLOCATION_STATUS_NOT_ASSIGNED.includes(record.status) ? (
          <span>Not yet assigned</span>
        ) : record.status ? (
          <AllocationChip resource={{ id: record.id, status: record.status }} />
        ) : (
          ""
        ),
    });
  if (visibleColumns.includes("product_set"))
    cols.push({
      key: "product_set",
      label: "Product Set",
      render: (record: any) =>
        UNALLOCATED_STATUSES.includes(record.status) ? (
          <span>Not yet assigned</span>
        ) : (
          <ProductInstanceIdField
            source="product_instance"
            record={record}
            label="Product Set"
          />
        ),
    });
  if (visibleColumns.includes("set_current_loc"))
    cols.push({
      key: "set_current_loc",
      label: "Set Current Loc",
      render: (record: any) => record.product_instance?.location?.name ?? "",
    });
  return cols;
};

const useListStyles = makeStyles((theme: Theme) => ({
  root: {
    "& > div:first-child": {
      display: "flex !important",
      flexDirection: "column !important",
      alignItems: "stretch !important",
      gap: `${theme.spacing(1)}px !important`,
      marginBottom: `${theme.spacing(1)}px !important`,
    },
    "& form": {
      order: "2 !important",
      minHeight: "0 !important",
    },
  },
}));

const AllocationListContent: FC<{
  groupBy: string;
  visibleColumns: string[];
  columns: ColumnDef[];
  groupConfig: GroupConfig | undefined;
  autoShowFilterKey?: string;
  autoShowColumnKey?: string;
  onToggleColumn: (key: string) => void;
}> = ({
  groupBy,
  visibleColumns,
  columns,
  groupConfig,
  autoShowFilterKey,
  autoShowColumnKey,
  onToggleColumn,
}) => {
  // Auto-show column when filter is active
  const { filterValues } = useListContext();

  useEffect(() => {
    if (!autoShowFilterKey || !autoShowColumnKey) return;
    const fv = filterValues as Record<string, any>;
    const hasFilter = !!fv?.[autoShowFilterKey];
    const isVisible = visibleColumns.includes(autoShowColumnKey);

    if (hasFilter && !isVisible) {
      onToggleColumn(autoShowColumnKey);
    }
  }, [
    filterValues,
    autoShowFilterKey,
    autoShowColumnKey,
    visibleColumns,
    onToggleColumn,
  ]);

  return (
    <Box>
      <DatagridTitle groupBy={groupBy} />
      {groupBy ? (
        <GroupedDatagrid columns={columns} group={groupConfig} />
      ) : (
        <Datagrid>
          {visibleColumns.includes("allocation_id") && <AllocationIdField />}
          {visibleColumns.includes("booking_id") && (
            <BookingField source="booking" />
          )}
          {visibleColumns.includes("product_name") && (
            <TextField source="product.name" label="Product Name" />
          )}
          {visibleColumns.includes("product_type") && (
            <TextField source="product.type" label="Product Type" />
          )}
          {visibleColumns.includes("business_unit") && (
            <FunctionField
              label="Business Unit"
              sortBy="product_instance.product.business_unit.name"
              render={(record: any) =>
                record.product_instance?.product?.business_unit?.name ?? ""
              }
            />
          )}
          {visibleColumns.includes("allocated_to") && (
            <FunctionField
              label="Allocated To"
              sortBy="booking.hospital_room.hospital.name"
              render={(record: any) => {
                const hospital = record.booking?.hospital_room?.hospital?.name;
                const room = record.booking?.hospital_room?.name;
                return hospital && room ? `${hospital} (${room})` : "";
              }}
            />
          )}
          {visibleColumns.includes("surgery_date") && (
            <FunctionField
              label="Surgery Date"
              sortBy="booking.date"
              render={(record: any) => <SurgeryDateField record={record} />}
            />
          )}
          {visibleColumns.includes("allocation_status") && (
            <FunctionField
              label="Allocation Status"
              sortBy="status"
              render={(record: any) =>
                ALLOCATION_STATUS_NOT_ASSIGNED.includes(record.status) ? (
                  <span>Not yet assigned</span>
                ) : record.status ? (
                  <AllocationChip
                    resource={{ id: record.id, status: record.status }}
                  />
                ) : (
                  ""
                )
              }
            />
          )}
          {visibleColumns.includes("product_set") && (
            <FunctionField
              label="Product Set"
              sortBy="product_instance.name"
              render={(record: any) =>
                UNALLOCATED_STATUSES.includes(record.status) ? (
                  <span>Not yet assigned</span>
                ) : (
                  <ProductInstanceIdField
                    source="product_instance"
                    record={record}
                  />
                )
              }
            />
          )}
          {visibleColumns.includes("set_current_loc") && (
            <FunctionField
              label="Set Current Loc"
              sortBy="product_instance.location.name"
              render={(record: any) => {
                const location = record.product_instance?.location?.name;
                return location ? `${location}` : "";
              }}
            />
          )}
        </Datagrid>
      )}
    </Box>
  );
};

const ResourceList: FC<ListProps> = (props) => {
  const { visibleColumns, toggleColumn, resetColumns } =
    useColumnVisibility(COLUMN_CHOICES);
  const [groupBy, setGroupBy] = useState("");
  const listClasses = useListStyles();
  const history = useHistory();
  const location = useLocation();

  const columns = getColumns(visibleColumns);
  const groupConfig = getGroupConfig(groupBy);
  const currentSort = getGroupSort(groupBy);

  const handleGroupByChange = (value: string) => {
    setGroupBy(value);
    const params = new URLSearchParams(location.search);
    params.delete("sort");
    params.delete("order");
    history.replace({ ...location, search: params.toString() });
  };

  return (
    <List
      {...props}
      key={groupBy}
      className={listClasses.root}
      exporter={false}
      sort={currentSort}
      filters={<ListFilter />}
      bulkActionButtons={false}
      actions={
        <ListActions
          visibleColumns={visibleColumns}
          onToggleColumn={toggleColumn}
          onResetColumns={resetColumns}
          groupBy={groupBy}
          onGroupByChange={handleGroupByChange}
        />
      }
    >
      <AllocationListContent
        groupBy={groupBy}
        visibleColumns={visibleColumns}
        columns={columns}
        groupConfig={groupConfig}
        autoShowFilterKey="product.type"
        autoShowColumnKey="product_type"
        onToggleColumn={toggleColumn}
      />
    </List>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ListFilter: FC<any> = (props) => (
  <Filter {...props}>
    <DateInput source="date_from" label="From Date" alwaysOn />
    <DateInput source="date_to" label="To Date" alwaysOn />
    <TextInput source="booking_id" label="Booking ID" />
    <ReferenceInput
      label="Product Name"
      source="product_id"
      reference="products"
      sort={{ field: "name", order: "ASC" }}
    >
      <AutocompleteInput
        optionText="name"
        shouldRenderSuggestions={(val: string) => {
          return val.trim().length > 2;
        }}
      />
    </ReferenceInput>
    <ReferenceInput
      label="Allocated To"
      source="booking.hospital_id"
      reference="hospitals"
      allowEmpty
    >
      <AutocompleteInput optionText="name" />
    </ReferenceInput>
    <ReferenceInput
      label="Product Set"
      source="product_instance_id"
      reference="product-instances"
      allowEmpty
    >
      <AutocompleteInput optionText="name" />
    </ReferenceInput>
    <ReferenceInput
      label="Set Current Loc"
      source="product_instance.location_id"
      reference="locations"
      allowEmpty
    >
      <AutocompleteInput optionText="name" />
    </ReferenceInput>
  </Filter>
);

const ResourceShow: FC<ShowProps> = (props) => {
  return (
    <>
      <CrudBreadcrumb
        resourceName={Common.resourceName}
        mode="show"
        resourceId={props.id}
      />
      <Show {...props} actions={<ShowActions />}>
        <SimpleShowLayout>
          <AllocationIdField />
          <FunctionField
            label="Product Name"
            render={(record: any) => record.product?.name ?? ""}
          />
          <ProductInstanceIdField source="product_instance" />
          <BookingCard source="booking" />
          <FunctionField
            label="From"
            render={(record: any) =>
              record.booking?.date
                ? moment(record.booking.date)
                    .tz("Asia/Singapore")
                    .format("DD/MM/YYYY - hh:mm A")
                : ""
            }
          />
          <FunctionField
            label="To"
            render={(record: any) =>
              record.end_time
                ? moment(record.end_time)
                    .tz("Asia/Singapore")
                    .format("DD/MM/YYYY - hh:mm A")
                : ""
            }
          />

          <DeliveryCard source="delivery" />
        </SimpleShowLayout>
      </Show>
    </>
  );
};

const ResourceEdit: FC<EditProps> = (props) => {
  return (
    <>
      <CrudBreadcrumb
        resourceName={Common.resourceName}
        mode="edit"
        resourceId={props.id}
      />
      <Edit {...props}>
        <SimpleForm redirect="show">
          <TextField source="id" />
          <TextInput source="name" />
        </SimpleForm>
      </Edit>
    </>
  );
};

const ShowActions: FC<ShowActionsProps> = (props) => {
  const { basePath, data, resource } = props;
  console.log(basePath, data, resource);
  const allocation = data as Allocation;

  if (!allocation) {
    return <Loading />;
  }

  return (
    <TopToolbar>
      <AssignButton data={allocation} />
      <UnAssignButton data={allocation} />
      <GotIssueButton data={allocation} />
      <ResolveIssueButton data={allocation} />
      <FinishButton data={allocation} />
      <CancelButton data={allocation} />
    </TopToolbar>
  );
};

const Common = {
  resourceName: "allocations",
};

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  name: Common.resourceName,
  icon: Icon,
  list: ResourceList,
  show: ResourceShow,
  edit: ResourceEdit,
};
